import React from 'react';
import Grid from '@mui/material/Grid';
import Alert from '@mui/material/Alert';
import Button from '@mui/material/Button';
import FormLabel from '@mui/material/FormLabel';
import OutlinedInput from '@mui/material/OutlinedInput';
import InputAdornment from '@mui/material/InputAdornment';
import IconButton from '@mui/material/IconButton';
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';
import Typography from '@mui/material/Typography';
import { generateRandomPassword } from 'src/util/util';
import { useAutoTranslation } from 'src/hooks/useTranslate';

export function PasswordStrengthAlert({ password }) {
  const t = useAutoTranslation();

  return (
    <Grid size={12}>
      <Alert severity="info">
        <Typography variant="body2" gutterBottom>
          {t('Password must be or contain:')}
        </Typography>
        <Typography
          variant="body2"
          sx={{
            display: 'block',
            color: password.length >= 8 ? 'green' : 'red'
          }}
        >
          {t('at least 8 characters long.')}
        </Typography>
        <Typography
          variant="body2"
          sx={{
            display: 'block',
            color: /[A-Z]/.test(password) ? 'green' : 'red'
          }}
        >
          {t('at least one upper case letter.')}
        </Typography>
        <Typography
          variant="body2"
          sx={{
            display: 'block',
            color: /[a-z]/.test(password) ? 'green' : 'red'
          }}
        >
          {t('at least one lower case letter.')}
        </Typography>
        <Typography
          variant="body2"
          sx={{
            display: 'block',
            color: /\d/.test(password) ? 'green' : 'red'
          }}
        >
          {t('at least one number.')}
        </Typography>
        <Typography
          variant="body2"
          sx={{
            display: 'block',
            color: /[^a-zA-Z0-9 ]/.test(password) ? 'green' : 'red'
          }}
        >
          {t('at least one special character.')}
        </Typography>
      </Alert>
    </Grid>
  );
}

export default function PasswordInput(props) {
  const t = useAutoTranslation();

  const {
    label,
    name,
    value,
    onChange,
    error,
    helperText,
    autoFocus,
    disabled,
    allowGenerate,
    setPassword
  } = props;
  const [showValue, setShowValue] = React.useState(false);

  return (
    <Grid
      container
      spacing={1}
      sx={{
        justifyContent: 'flex-start',
        alignItems: 'flex-start'
      }}
    >
      {label && (
        <Grid
          container
          spacing={2}
          sx={{
            alignItems: 'baseline'
          }}
        >
          <Grid>
            <FormLabel component="label" className="form-label">
              {label}
            </FormLabel>
          </Grid>
          {allowGenerate && (
            <Grid>
              <Button
                size="small"
                color="primary"
                className="button-no-transformation"
                onClick={() => setPassword(generateRandomPassword())}
              >
                {t('Generate randomly')}
              </Button>
            </Grid>
          )}
        </Grid>
      )}
      <Grid size={12}>
        <OutlinedInput
          fullWidth
          type={showValue ? 'text' : 'password'}
          value={value || ''}
          onChange={onChange}
          name={name}
          error={!!error}
          inputProps={{ 'aria-label': props.ariaLabel || t('enter password') }}
          // autoFocus={autoFocus ?? undefined}
          disabled={disabled ?? undefined}
          endAdornment={
            <InputAdornment position="end">
              <IconButton
                aria-label={t('toggle password visibility')}
                onClick={() => setShowValue((prevValue) => !prevValue)}
                onMouseDown={(e) => e.preventDefault()}
                size="large"
              >
                {showValue ? (
                  <Visibility fontSize="small" />
                ) : (
                  <VisibilityOff fontSize="small" />
                )}
              </IconButton>
            </InputAdornment>
          }
        />
        {helperText && (
          <Typography variant="caption" color={error ? 'error' : undefined}>
            {helperText}
          </Typography>
        )}
      </Grid>
    </Grid>
  );
}
